// «Documentación al día» como archivo organizado: área → entidad → apartado → documentos.
import { STATUS, CONTRATOS_KEY } from "@/mock/data";
import {
  getMatterDoc, seccionVivienda, seccionVehiculo, seccionPersona, seccionMascota,
  VIVIENDA_SECCIONES, VEHICULO_SECCIONES, PERSONA_SECCIONES, MASCOTA_SECCIONES,
} from "@/lib/clasificacion";

export const AREAS_ARCHIVO = [
  { id: "viviendas", label: "Viviendas", icon: "Home" },
  { id: "vehiculos", label: "Vehículos", icon: "Car" },
  { id: "ocio", label: "Ocio y Reservas", icon: "Ticket" },
  { id: "contratos", label: "Contratos y servicios personales", icon: "FileSignature" },
  { id: "familia", label: "Familia", icon: "Users" },
  { id: "mascotas", label: "Mascotas", icon: "PawPrint" },
  { id: "pendientes", label: "Pendientes de clasificar", icon: "Inbox" },
];

export const OCIO_SECCIONES = [{ id: "entradas", label: "Entradas y reservas", icon: "Ticket" }];

export const FAMILIA_SECCIONES = [{ id: "documentos", label: "Documentos", icon: "FolderOpen" }];

// Módulo de un asunto (misma regla que el Archivo). Sin entidad válida → pendientes.
export const moduloDeAsunto = (m, { viviendas, vehiculos, pets, familyMembers }) => {
  const has = (list, id) => !!id && (list || []).some((x) => x.id === id);
  const c = m.category;
  if (c === "Hogar" && has(viviendas, m.vivienda_id)) return "viviendas";
  if (c === "Vehículos" && has(vehiculos, m.vehiculo_id)) return "vehiculos";
  if (c === "Mascotas" && has(pets, m.pet_id)) return "mascotas";
  if (c === CONTRATOS_KEY && has(familyMembers, m.family_member_id)) return "contratos";
  if (c === "Ocio") return "ocio";
  if (c === "Otros" && has(familyMembers, m.family_member_id)) return "familia";
  return "pendientes";
};

const matterItem = (m, doc) => ({ key: `m-${m.id}`, kind: "matter", matter: m, date: m.created_at || "", docId: doc && !doc.is_deleted ? doc.id : null });
// Solo cuentan documentos reales almacenados, una vez cada uno.
export const docIdsDe = (items) => new Set(items.map((it) => it.docId).filter(Boolean));
const union = (sets) => new Set(sets.flatMap((x) => [...x]));

// Contenedor: { [area]: { [entidadId]: { label, secciones: {id: items[]} } } } + pendientes[]
export const buildArchivo = ({ matters, documents, viviendas, vehiculos, pets, familyMembers }) => {
  const tree = {};
  const pendientes = [];
  const ent = (area, id, label, secciones) => {
    tree[area] = tree[area] || {};
    if (!tree[area][id]) {
      tree[area][id] = { id, label, seccionesDef: secciones, items: {} };
      secciones.forEach((s) => (tree[area][id].items[s.id] = []));
    }
    return tree[area][id];
  };
  const fam = (id) => (familyMembers || []).find((f) => f.id === id);
  const viv = (id) => (viviendas || []).find((v) => v.id === id);
  const veh = (id) => (vehiculos || []).find((v) => v.id === id);
  const pet = (id) => (pets || []).find((p) => p.id === id);

  (matters || []).filter((m) => m.status === STATUS.CONTROLLED).forEach((m) => {
    const doc = getMatterDoc(m, documents);
    const it = matterItem(m, doc);
    const c = m.category;
    if (c === "Hogar" && viv(m.vivienda_id)) {
      ent("viviendas", m.vivienda_id, viv(m.vivienda_id).alias, VIVIENDA_SECCIONES).items[seccionVivienda(m, doc)].push(it);
    } else if (c === "Vehículos" && veh(m.vehiculo_id)) {
      ent("vehiculos", m.vehiculo_id, veh(m.vehiculo_id).alias, VEHICULO_SECCIONES).items[seccionVehiculo(m, doc)].push(it);
    } else if (c === "Mascotas" && pet(m.pet_id)) {
      ent("mascotas", m.pet_id, pet(m.pet_id).name, MASCOTA_SECCIONES).items[seccionMascota(m, doc)].push(it);
    } else if (c === CONTRATOS_KEY && fam(m.family_member_id)) {
      ent("contratos", m.family_member_id, fam(m.family_member_id).name, PERSONA_SECCIONES).items[seccionPersona(m, doc)].push(it);
    } else if (c === "Otros" && fam(m.family_member_id)) {
      ent("familia", m.family_member_id, fam(m.family_member_id).name, FAMILIA_SECCIONES).items.documentos.push(it);
    } else {
      pendientes.push(it);
    }
  });

  const areas = AREAS_ARCHIVO.map((a) => {
    if (a.id === "pendientes") {
      const ids = docIdsDe(pendientes);
      return { ...a, docIds: ids, count: ids.size, total: pendientes.length, items: pendientes };
    }
    const ents = Object.values(tree[a.id] || {}).map((e) => {
      const ids = docIdsDe(Object.values(e.items).flat());
      return { ...e, docIds: ids, count: ids.size, total: Object.values(e.items).flat().length };
    }).filter((e) => e.total > 0).sort((x, y) => x.label.localeCompare(y.label, "es"));
    const ids = union(ents.map((e) => e.docIds));
    return { ...a, entidades: ents, docIds: ids, count: ids.size, total: ents.reduce((n, e) => n + e.total, 0) };
  });
  return areas.filter((a) => a.total > 0);
};

export const totalDocsArchivo = (areas) => union(areas.map((a) => a.docIds)).size;

export const porFechaDesc = (items) => [...items].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
